import { formatDistanceToNow } from 'date-fns'
import { useState } from 'react'
import { useCampaignRecipients } from '@/api/hooks/use-campaigns'
import { DataTable } from '@/components/ui/data-table'
import { StatusBadge } from '@/components/ui/status-badge'
import type { CampaignRecipient } from '@/types/newsletter'

const STATUS_OPTIONS = ['all', 'delivered', 'opened', 'bounced', 'skipped'] as const

interface CampaignRecipientsTabProps {
  campaignId: string
}

export function CampaignRecipientsTab({
  campaignId,
}: CampaignRecipientsTabProps): React.JSX.Element {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const { data, isLoading } = useCampaignRecipients(campaignId, {
    page,
    per_page: 25,
    status: statusFilter === 'all' ? undefined : statusFilter,
  })

  const recipients = data?.data ?? []
  const pagination = data?.pagination

  function formatTimestamp(value: string | null): string {
    if (!value) return '-'
    return formatDistanceToNow(new Date(value), { addSuffix: true })
  }

  const columns = [
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (row: CampaignRecipient) => <span className="font-medium">{row.email}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (row: CampaignRecipient) => (
        <StatusBadge status={row.status}>{row.status}</StatusBadge>
      ),
    },
    {
      key: 'delivered_at',
      label: 'Delivered',
      render: (row: CampaignRecipient) => (
        <span className="text-sm text-text-secondary">{formatTimestamp(row.delivered_at)}</span>
      ),
    },
    {
      key: 'opened_at',
      label: 'Opened',
      render: (row: CampaignRecipient) => (
        <span className="text-sm text-text-secondary">{formatTimestamp(row.opened_at)}</span>
      ),
    },
    {
      key: 'clicked_at',
      label: 'Clicked',
      render: (row: CampaignRecipient) => (
        <span className="text-sm text-text-secondary">{formatTimestamp(row.clicked_at)}</span>
      ),
    },
    {
      key: 'bounced_at',
      label: 'Bounced',
      render: (row: CampaignRecipient) => (
        <span className="text-sm text-text-secondary">{formatTimestamp(row.bounced_at)}</span>
      ),
    },
    {
      key: 'skipped_reason',
      label: 'Skipped Reason',
      render: (row: CampaignRecipient) => (
        <span className="text-xs text-text-tertiary">{row.skipped_reason ?? '-'}</span>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPage(1)
          }}
          className="rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary focus:border-brand-500 focus:outline-none"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s === 'all' ? 'All statuses' : s}
            </option>
          ))}
        </select>
      </div>

      <DataTable
        columns={columns}
        data={recipients}
        isLoading={isLoading}
        emptyMessage="No recipients found"
        pagination={
          pagination
            ? { page: pagination.page, perPage: pagination.per_page, total: pagination.total }
            : undefined
        }
        onPageChange={setPage}
      />
    </div>
  )
}
